import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { validateDocumentToken, getPresignedUrl, confirmUpload } from '../utils/api';
import { usePageTracking, useStepTimer, useAnalytics } from '../hooks/useAnalytics';

type UploadStatus = 'idle' | 'validating' | 'ready' | 'uploading' | 'success' | 'error';

const Documents: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [applicantName, setApplicantName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  usePageTracking('Documents (Legacy)');
  const { trackDocumentUploadStart, trackDocumentUploadComplete, trackDocumentUploadError, trackDocumentValidationError } = useAnalytics();
  const uploadStartTime = useRef<number>(0);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No token provided. Please use the link from your email.');
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) return;

    setStatus('validating');
    try {
      const response = await validateDocumentToken(token);

      if (response.valid) {
        setApplicantName(response.applicantName || 'Applicant');
        setStatus('ready');
      } else {
        setStatus('error');
        setErrorMessage(response.error || 'Invalid or expired token. Please contact support.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to validate token. Please try again.');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        trackDocumentValidationError('insurance', 'File too large');
        setErrorMessage('File size must be less than 10MB');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        trackDocumentValidationError('insurance', 'Invalid file type');
        setErrorMessage('Please upload a PDF, JPG, PNG, HEIC, or WebP file');
        return;
      }

      setSelectedFile(file);
      setErrorMessage('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) return;

    trackDocumentUploadStart('insurance');
    uploadStartTime.current = Date.now();
    setStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');

    try {
      // Step 1: Get presigned URL from backend
      const { presignedUrl, s3Key } = await getPresignedUrl(
        token,
        'insurance',
        selectedFile.name
      );

      // Step 2: Upload directly to S3 using presigned URL
      await axios.put(presignedUrl, selectedFile, {
        headers: {
          'Content-Type': selectedFile.type,
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      // Step 3: Confirm upload with backend
      await confirmUpload({
        token,
        s3Key,
        documentType: 'insurance',
      });

      setStatus('success');
      setUploadProgress(100);
      trackDocumentUploadComplete({ documentType: 'insurance', fileSize: selectedFile.size, uploadDuration: Math.floor((Date.now() - uploadStartTime.current) / 1000) });
    } catch (error) {
      console.error('Upload error:', error);
      setStatus('error');
      trackDocumentUploadError({ documentType: 'insurance', errorType: 'upload_failed', errorMessage: axios.isAxiosError(error) ? error.response?.data?.error || 'Upload failed' : 'Unexpected error' });
      setErrorMessage(
        axios.isAxiosError(error)
          ? error.response?.data?.error || 'Failed to upload document. Please try again.'
          : 'An unexpected error occurred. Please try again.'
      );
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="card p-8">
          <h1 className="text-3xl font-bold text-charcoal mb-2">Upload Your Documents</h1>
          <p className="text-gray-600 mb-8">
            Please upload your proof of insurance to continue with your application.
          </p>

          {status === 'validating' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-green mx-auto mb-4"></div>
              <p className="text-gray-600">Validating your access...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4 mb-6">
              <p className="font-semibold">Error</p>
              <p>{errorMessage}</p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <div className="bg-logo-green/10 border border-logo-green text-logo-green rounded-lg p-4 mb-6">
                <p className="font-semibold">Welcome{applicantName ? `, ${applicantName}` : ''}!</p>
                <p>Your access has been verified. Please upload your insurance document below.</p>
              </div>

              <div className="mb-6">
                <label className="block text-charcoal font-semibold mb-2">
                  Insurance Document
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Accepted formats: PDF, JPG, PNG, HEIC, WebP (max 10MB)
                </p>

                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-logo-green file:text-white
                    hover:file:bg-shadow-green
                    file:cursor-pointer cursor-pointer"
                />
              </div>

              {selectedFile && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">Selected file:</p>
                  <p className="font-semibold text-charcoal">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              {errorMessage && (
                <div className="bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4 mb-6">
                  <p>{errorMessage}</p>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={!selectedFile}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload Document
              </button>
            </>
          )}

          {status === 'uploading' && (
            <div className="py-8">
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-charcoal">Uploading...</span>
                  <span className="text-sm font-medium text-charcoal">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-logo-green h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
              <p className="text-center text-gray-600 text-sm">
                Please wait while we upload your document...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-logo-green/10 rounded-full mb-4">
                <svg className="w-8 h-8 text-logo-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Upload Successful!</h2>
              <p className="text-gray-600 mb-6">
                Your insurance document has been uploaded successfully. We'll review it and get back to you soon.
              </p>
              <p className="text-sm text-gray-500">
                You will receive an email with your next steps shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Documents;
